import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Button,
  Alert,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService } from '../services/api';

const DeleteProperty = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [deletingPropertyId, setDeletingPropertyId] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const isAuth = apiService.isAuthenticated();
    if (!isAuth) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    const adminUser = apiService.getAdminUser();
    if (adminUser && adminUser.id) {
      setIsAuthorized(true);
      fetchAllProperties();
    } else {
      setIsAuthorized(false);
      setLoading(false);
    }
  }, []);

  const fetchAllProperties = async () => {
    try {
      setLoading(true);
      const response = await apiService.getProperties('all');
      if (response.success && response.data) {
        setProperties(response.data);
        
        if (response.data.length === 0) {
          setAlert({
            severity: 'info',
            message: 'No properties found'
          });
        }
      } else {
        throw new Error(response.message || 'Failed to fetch properties');
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to load properties: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProperty = (propertyId) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProperties.length === properties.length) {
      setSelectedProperties([]);
    } else {
      setSelectedProperties(properties.map((p) => p.id));
    }
  };

  const handleDeleteClick = (property) => {
    setDeletingPropertyId(property.id);
    setDeleteDialogOpen(true);
  };

  const handleBatchDelete = () => {
    if (selectedProperties.length === 0) {
      setAlert({
        severity: 'warning',
        message: 'Please select at least one property to delete'
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const idsToDelete = deletingPropertyId ? [deletingPropertyId] : selectedProperties;
      let successCount = 0;
      let failCount = 0;

      for (const propertyId of idsToDelete) {
        try {
          const response = await apiService.deleteProperty(propertyId);
          if (response.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error('Failed to delete property:', propertyId, error);
          failCount++;
        }
      }

      setAlert({
        severity: failCount > 0 ? 'warning' : 'success',
        message: `Deleted: ${successCount}${failCount > 0 ? `, Failed: ${failCount}` : ''}`
      });

      setDeleteDialogOpen(false);
      setDeletingPropertyId(null);
      setSelectedProperties([]);
      
      await fetchAllProperties();
    } catch (error) {
      console.error('Error deleting properties:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to delete property(ies): ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        py: 6,
        textAlign: 'center'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          🔒 Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You must be logged in as an admin to view this section.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (properties.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No properties found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      <Alert severity="error" sx={{ mb: 3 }}>
        ⚠️ <strong>Warning!</strong> Deleted properties will be moved to trash and will not appear in public listings. 
        You can still recover them if needed.
      </Alert>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleSelectAll}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          {selectedProperties.length === properties.length ? 'Deselect All' : 'Select All'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {selectedProperties.length} selected of {properties.length}
        </Typography>
        {selectedProperties.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBatchDelete}
            disabled={loading}
            sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 } }}
          >
            Delete Selected ({selectedProperties.length})
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#fff3e0' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedProperties.length > 0 && selectedProperties.length < properties.length}
                  checked={selectedProperties.length === properties.length && properties.length > 0}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {properties.map((property) => (
              <TableRow
                key={property.id}
                sx={{
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: '#fff9f9',
                    boxShadow: 1
                  },
                  bgcolor: selectedProperties.includes(property.id) ? '#ffebee' : 'transparent'
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedProperties.includes(property.id)}
                    onChange={() => handleSelectProperty(property.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {property.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {property.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    📍 {property.location}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 700 }}>
                    ₹{parseFloat(property.price).toLocaleString('en-IN')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={property.category} 
                    size="small" 
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {property.ownerName || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(property)}
                    disabled={loading}
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 3
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingPropertyId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          🗑️ Delete Property(ies)
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {deletingPropertyId ? (
            <Typography color="text.secondary">
              Are you sure you want to delete this property? 
              <br /><br />
              The property will be moved to trash and <strong>won't appear in public listings</strong>. 
              You can still access it from trash if needed.
            </Typography>
          ) : (
            <Typography color="text.secondary">
              Are you sure you want to delete <strong>{selectedProperties.length} property(ies)</strong>? 
              <br /><br />
              They will be moved to trash and <strong>won't appear in public listings</strong>. 
              You can still access them from trash if needed.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletingPropertyId(null);
            }}
            color="primary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteProperty;